import React, { useState, useEffect } from 'react';
import { Table, Tag, Tabs, Empty, Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { formatCurrency } from './MaturityTableUtils';
import TransactionService from '../../service/transaction-service/Transaction.service';
import PaymentService from '../../service/payment-service/Payment.service';
import { ReloadOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;

interface DeletedTransactionSubTableProps {
  maturityScheduleId: number;
}

interface DeletedTransaction {
  id: number;
  transactionDate: string;
  amount: number;
  transactionType: string;
  feeApplied: number;
  balance: number;
  status: string;
  employeeName: string;
  deletedAt: string | null;
  deletedByName?: string;
}

interface DeletedPayment {
  id: number;
  amount: number;
  paymentDate: string;
  status: string;
  originalStatus: string;
  deletedAt: string | null;
  deletedByName?: string;
}

const DeletedTransactionSubTable: React.FC<DeletedTransactionSubTableProps> = ({
  maturityScheduleId,
}) => {
  const [deletedTransactions, setDeletedTransactions] = useState<
    DeletedTransaction[]
  >([]);
  const [deletedPayments, setDeletedPayments] = useState<DeletedPayment[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('transactions');

  const fetchDeletedTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const response =
        await TransactionService.getDeletedTransactionsByMaturityScheduleWithEmployee(
          maturityScheduleId
        );
      const transactions = response?.data || [];
      // Map to ensure type compatibility
      const mappedTransactions = transactions.map((transaction: any) => ({
        ...transaction,
        deletedAt: transaction.deletedAt || '',
      }));
      setDeletedTransactions(mappedTransactions);
    } catch (error) {
      console.error('Error fetching deleted transactions:', error);
      setDeletedTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchDeletedPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response =
        await PaymentService.getDeletedPaymentsByMaturitySchedule(
          maturityScheduleId
        );
      const payments = response?.data || [];
      // Map to ensure type compatibility
      const mappedPayments = payments.map((payment: any) => ({
        ...payment,
        deletedAt: payment.deletedAt || '',
      }));
      setDeletedPayments(mappedPayments);
    } catch (error) {
      console.error('Error fetching deleted payments:', error);
      setDeletedPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'transactions') {
      fetchDeletedTransactions();
    } else if (key === 'payments') {
      fetchDeletedPayments();
    }
  };

  // Fetch data for initial tab
  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchDeletedTransactions();
    } else if (activeTab === 'payments') {
      fetchDeletedPayments();
    }
  }, [maturityScheduleId]);

  const handleRefresh = () => {
    if (activeTab === 'transactions') {
      fetchDeletedTransactions();
    } else if (activeTab === 'payments') {
      fetchDeletedPayments();
    }
  };

  const transactionColumns: ColumnsType<DeletedTransaction> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Ngày giao dịch',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Loại giao dịch',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 120,
      render: (type: string) => (
        <Tag color={type === 'deposit' ? 'green' : 'blue'}>
          {type === 'deposit' ? 'Nạp tiền' : 'Rút tiền'}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number, record) => (
        <span
          className={
            record.transactionType === 'deposit'
              ? 'text-green-600'
              : 'text-blue-600'
          }
        >
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Phí',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 100,
      render: (fee: number) => formatCurrency(fee || 0),
    },
    {
      title: 'Số dư',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: number) => (
        <span className="font-medium text-blue-600">
          {formatCurrency(balance)}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag
          color={
            status === 'COMPLETED'
              ? 'green'
              : status === 'PENDING'
                ? 'orange'
                : 'red'
          }
        >
          {status === 'COMPLETED'
            ? 'Hoàn thành (Đã xóa)'
            : status === 'PENDING'
              ? 'Đang xử lý (Đã xóa)'
              : 'Đã hủy (Đã xóa)'}
        </Tag>
      ),
    },
    {
      title: 'Nhân viên',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 120,
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 140,
      render: (deletedByName: string | null) => deletedByName || '-',
    },
    {
      title: 'Thời gian xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 140,
      render: (deletedAt: string | null) => (
        <span className="text-gray-500 text-sm">
          {deletedAt ? dayjs(deletedAt).format('DD/MM/YYYY HH:mm') : '-'}
        </span>
      ),
    },
  ];

  const paymentColumns: ColumnsType<DeletedPayment> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Ngày thanh toán',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 140,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="text-blue-600 font-medium">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Trạng thái gốc',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 120,
      render: (status: string) => (
        <Tag
          color={
            status === 'COMPLETED'
              ? 'green'
              : status === 'PENDING'
                ? 'orange'
                : 'red'
          }
        >
          {status === 'COMPLETED'
            ? 'Hoàn thành (Đã xóa)'
            : status === 'PENDING'
              ? 'Đang xử lý (Đã xóa)'
              : 'Đã hủy (Đã xóa)'}
        </Tag>
      ),
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 140,
      render: (deletedByName: string | null) => deletedByName || '-',
    },
    {
      title: 'Thời gian xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 140,
      render: (deletedAt: string | null) => (
        <span className="text-gray-500 text-sm">
          {deletedAt ? dayjs(deletedAt).format('DD/MM/YYYY HH:mm') : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="flex-1"
        >
          <TabPane tab="Giao dịch đã xóa" key="transactions" />
          <TabPane tab="Thanh toán đã xóa" key="payments" />
        </Tabs>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          type="primary"
          size="small"
          className="ml-4"
        >
          Làm mới
        </Button>
      </div>

      {activeTab === 'transactions' && (
        <>
          {deletedTransactions.length > 0 ? (
            <Table
              columns={transactionColumns}
              dataSource={deletedTransactions}
              rowKey="id"
              loading={transactionsLoading}
              pagination={{
                pageSize: 5,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} giao dịch`,
              }}
              scroll={{ x: 900 }}
              size="small"
              className="border border-gray-200 rounded"
            />
          ) : (
            <Empty description="Không có giao dịch đã xóa" className="py-8" />
          )}
        </>
      )}

      {activeTab === 'payments' && (
        <>
          {deletedPayments.length > 0 ? (
            <Table
              columns={paymentColumns}
              dataSource={deletedPayments}
              rowKey="id"
              loading={paymentsLoading}
              pagination={{
                pageSize: 5,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} của ${total} thanh toán`,
              }}
              scroll={{ x: 600 }}
              size="small"
              className="border border-gray-200 rounded"
            />
          ) : (
            <Empty description="Không có thanh toán đã xóa" className="py-8" />
          )}
        </>
      )}
    </div>
  );
};

export default DeletedTransactionSubTable;
